import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { withRouter } from 'react-router';
import { attach } from '@mgc/map-client-shared/lib/ReduxStore';
import { mapSelectors } from '@mgc/map-client-shared/lib/ReduxTools';
import MapPropTypes from '../../MapPropTypes';
import selectors from '../../../selectors';
import CartActions from '../../../actions/cart';
import UiActions from '../../../actions/ui';
import HelpActions from '../../../actions/help';
import strings from '../../../strings';
import ShoppingCartModalBase from '../ShoppingCartBase';
import Summary from './Summary';
import Progress from './Progress';
import './ShoppingCartModal.scss';

class ShoppingCartModal extends Component {
    static propTypes = {
        // functions
        close: PropTypes.func.isRequired,        
        saveShoppingCart: PropTypes.func.isRequired,        
        onEditCartChange: PropTypes.func,
        updateInstructions: PropTypes.func.isRequired,
        updateSaveForLaterList: PropTypes.func.isRequired,
        removeItemFromCart: PropTypes.func.isRequired,
        finishOrder: PropTypes.func.isRequired,
        openHelpModal: PropTypes.func.isRequired,
        openCheckModal: PropTypes.func.isRequired,        
        // data
        cart: PropTypes.arrayOf(
            PropTypes.shape({
                item: MapPropTypes.CartItemProps,
                product: MapPropTypes.PricingProps,
            })
        ).isRequired,
        cartTotal: PropTypes.number.isRequired,
        offer: MapPropTypes.OfferProps,
        orderStarted: PropTypes.bool.isRequired,
        orderComplete: PropTypes.bool.isRequired,
        orderIsSuccessful: PropTypes.bool.isRequired,
        orderInProgress: PropTypes.bool.isRequired,
        orderFailed: PropTypes.bool.isRequired,
        visible: PropTypes.bool.isRequired,
        user: MapPropTypes.UserProps,
        history: PropTypes.object,
        isSucessful: PropTypes.bool,
        soldTo: PropTypes.string,
    };

    static mapState = (state) =>
        mapSelectors(state, {
            cart: selectors.cart.getCartContents,
            soldTo: selectors.cart.getSoldTo,
            cartTotal: selectors.cart.getTotal,
            offer: selectors.offer.getSelectedOffer,
            orderStarted: selectors.order.orderStarted,
            orderComplete: selectors.order.orderIsComplete,
            orderIsSuccessful: selectors.order.orderIsSuccessful,
            orderInProgress: selectors.order.orderInProgress,
            orderFailed: selectors.order.orderFailed,
            visible: selectors.ui.getShoppingCartModalVisible,
            user: selectors.user.getMyself,
        });

    static mapDispatch = (dispatch) => ({
        close: () => dispatch(UiActions.closeShoppingCartModal()),
        saveShoppingCart: (userId) =>
            dispatch(CartActions.saveShoppingCart(userId)),        
        finishOrder: (history) => dispatch(UiActions.finishOrder(history)),
        openCheckModal: (userId) => {
            dispatch(CartActions.saveShoppingCart(userId));
            dispatch(UiActions.closeModal('shoppingcart'));
            dispatch(UiActions.openModal('checkout'));
        },
        openHelpModal: () => {
            dispatch(HelpActions.showContactTab());
            dispatch(
                HelpActions.updateContactSubjectIndex(
                    HelpActions.contactSubjects.indexOf('Support Inquiry')
                )
            );
            dispatch(UiActions.closeCheckoutModal());
            dispatch(UiActions.openHelpModal());
        },
        updateInstructions: (instructions) =>
            dispatch(CartActions.updateCartInstructions(instructions)),
        updateSaveForLaterList: (itemId, flag) =>
            dispatch(CartActions.updateSaveForLaterList(itemId, flag)),
        removeItemFromCart: (itemIndex, cartId) =>      
            dispatch(CartActions.removeItemFromCart(itemIndex, cartId)),
    });

    _handleContinueShoppingOrCloseClick() {
        this.props.saveShoppingCart(this.props.user.id);
        this.props.close();
    }

    removeItemFromCartAfterSaveCart = (itemIndex = -1, cartId = '', saveForLater = false) => {
        this.props.saveShoppingCart(this.props.user.id);
        if(itemIndex == -1 && cartId == ''){
            const cartIds = this.props.cart.filter((item) => item.addedToCart && item.saveForLater == saveForLater).map((item)=> item.cartId);
            this.props.removeItemFromCart(itemIndex, cartIds);
        }else        
            this.props.removeItemFromCart(itemIndex, cartId);
        // Place holder to close the pop if cart items are all empty in both sections - To be written if business asks for it.
    }

    onEditOrder = () => {
        this.props.saveShoppingCart(this.props.user.id); // Save the shopping cart        
        this.props.onEditCartChange(true);
        this.props.close(); // Close the modal
    };

    _handlePrintClick() {
        this.props.finishOrder(this.props.history);
        // if we print() right away, then we get a nice printout of the modal.
        // so we'll wait for the page to re-render before printing.
        // secret bonus: after an order, the user can print a receipt from the /order
        // or /checkout screens *at any time*. we store the last transaction made
        // in our redux store. it's used to fill out the receipt.
        setTimeout(() => window.print(), 250);
    }

    _handleInstructionsUpdate(event) {
        this.props.updateInstructions(event.target.value);
    }

    _closeButtonClass() {
        if (this.props.orderFailed) {
            return 'btn-danger';
        }
        if (this.props.isSucessful) {
            return 'btn-success';
        }
        return 'btn-warning';
    }

    _renderContent() {
        //console.log('sold to in checkout model=', this.props.soldTo)
        
        if (this.props.orderStarted) {
            return (
                <Progress
                    complete={this.props.orderComplete}
                    failed={this.props.orderFailed}
                    isSuccessful={this.props.orderIsSuccessful}
                    openHelp={() => this.props.openHelpModal()}
                />
            );
        }

        return (
            <Summary
                cart={this.props.cart}
                cartTotal={this.props.cartTotal}
                offer={this.props.offer}
                onUpdateInstructions={(e) => this._handleInstructionsUpdate(e)}
                updateSaveForLater={this.props.updateSaveForLaterList} // Pass the function
                soldTo={this.props.soldTo}
                onEditOrder={this.onEditOrder}
                removeItemFromCart={(...args) => this.removeItemFromCartAfterSaveCart(...args)}                
            />
        );
    }

    render() {
        if (!this.props.offer) {
            return <span />;
        }

        const itemsToPurchase = this.props.cart.filter((item) => !item.saveForLater && item.addedToCart);
        // Disable checkout if there are no items to purchase, or if any item has an
        // end date that is less than or equal to its start date (invalid date range).
        const hasInvalidDateRange = itemsToPurchase.some(
            (item) =>
                !item.startDate ||
                !item.endDate ||
                moment(item.endDate).isSameOrBefore(moment(item.startDate))
        );
        const areCartActionsDisabled = itemsToPurchase.length === 0 || hasInvalidDateRange;

        const actionButton = (
            <button
                className="btn btn-success"
                onClick={() => this.props.openCheckModal(this.props.user.id)}
                disabled={areCartActionsDisabled || this.props.orderInProgress}
            >
                {strings.checkout}
            </button>
        );          
          
        return (
            <ShoppingCartModalBase
                modalClassName="shoppingcart-modal"
                modalTitle={strings.reviewCart}
                visible={this.props.visible}
                backdrop="static"
                closeModal={() => this._handleContinueShoppingOrCloseClick()}
            >
                {this._renderContent()}
                <div className="cart-footer">
                        <button
                            className={`btn ${this._closeButtonClass()}`}
                            onClick={() =>
                                this._handleContinueShoppingOrCloseClick()
                            }
                            disabled={areCartActionsDisabled || this.props.orderInProgress}
                        >
                            {strings.continueShopping}
                        </button>
                        &nbsp;
                        {actionButton}
                </div>
            </ShoppingCartModalBase>
        );
    }
}

export default withRouter(attach(ShoppingCartModal));



// WEBPACK FOOTER //
// ./app/components/Modals/ShoppingCartModal/index.jsx